import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Grid,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CampaignGroup from "./Forms/CampaignGroup";
import TypeCards from "./TypeCards";
import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
} from "@mui/x-data-grid-premium";
import MonthlyConstants from "./Forms/MonthlyConstants";

import { calculator } from "./CampaignCalculations";

import dayjs from "dayjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import withAuth from "../../../../../../Hoc/withAuth";
import AlertPopup from "../../../../../../Components/Bits/AlertPopup";
import DuplicatePrevious from "./DuplicatePrevious";

// const aggregationFunctionsByField = {
//   // Sums

//   currentSeats: ["sum"],
//   hours: "sum",
//   sales: ["sum"],
//   totalPremium: ["sum"],
//   totalLeadsRequired: ["sum"],
//   confirmedLeads: ["sum"],
//   requiredLessConfirmedLeads: ["sum"],
//   additionalSeatsRequiredToMaxLeads: ["sum"],
//   capacityIncomeRisk: ["sum"],
//   additionalLeadsRequiredToMaxSeats: ["sum"],
//   leadsIncomeRisk: ["sum"],
//   totalBi: ["sum"],

//   // Averages
//   averagePremium: ["avg"], // Custom average: totalPremium/sales
//   biPerSale: ["avg"],
//   biPerHour: ["avg"],
//   biPerSeat: ["avg"],
//   salesPerHour: ["avg"],
//   salesToContactRatio: ["avg"],
//   salesPerLead: ["avg"],
//   contactToLeads: ["avg"],
//   leadsRequiredPerSeat: ["avg"],
// };

const currencyFormatter = (value) => {
  if (typeof value !== "number") return value;
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
  }).format(value);
};

const PlanningContainer = ({ month, user }) => {
  const token = user.token;
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const baseDate = dayjs(month);
  const queryClient = useQueryClient();

  const theme = useTheme();

  const [selectedCard, setSelectedCard] = React.useState({
    title: `Current - ${baseDate.format("MMMM YYYY")}`,
    date: baseDate.startOf("month").format("YYYY-MM-DD"),
    type: "current",
    color: theme.palette.primary.main,
  });

  const queryKey = [
    "campaignPlanningData",
    selectedCard.date,
    selectedCard.type,
  ];

  const getCampaignByTypeAndDate = useQuery({
    queryKey: queryKey, // Use the new, cleaner query key
    queryFn: async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/campaignPlanning2`,
          {
            // Use the state's date and type directly
            params: {
              planningType: selectedCard.type,
              date: selectedCard.date,
            },
            ...config,
          },
        );
        return response?.data ?? [];
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        return [];
      }
    },
    onSuccess: (data) => {
      setDataState(data || []);
    },
    enabled: !!selectedCard?.type && !!selectedCard?.date, // Check both
  });

  const deletePlanningCampaign = useMutation({
    mutationFn: async (id) => {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/campaignPlanning2/${id}`,
        config,
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the query to refetch the campaigns
      queryClient.invalidateQueries({ queryKey: queryKey });
    },
    onError: (error) => {
      console.error("Error deleting campaign:", error);
      // Optionally, you can show an error notification here
    },
  });

  const getMonthlyConstants = useQuery({
    queryKey: ["monthlyConstants", month, selectedCard.type],
    queryFn: async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/campaignPlanning2/monthDefaults/${month}/${selectedCard.type}`,
        config,
      );

      return response.data;
    },
    onSuccess: (data) => {
      if (
        data &&
        data.workingDays !== undefined &&
        data.workingHoursPerDay !== undefined
      ) {
        setMonthDefaults({
          workingDays: data.workingDays,
          workingHoursPerDay: data.workingHoursPerDay,
        });
      } else {
        setMonthDefaults({
          workingDays: 0,
          workingHoursPerDay: 0,
        });
      }
    },
    onError: (error) => {
      console.error("Error fetching monthly constants:", error);
      // On error, also reset to defaults
      setMonthDefaults({
        workingDays: 0,
        workingHoursPerDay: 0,
      });
    },

    retry: 1, // Retry once on failure
  });

  let monthDefaultsCheck = getMonthlyConstants.data || null;

  const createMonthlyConstants = useMutation({
    mutationFn: async (newConstants) => {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/campaignPlanning2/monthDefaults`,
        newConstants,
        config,
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the query to refetch the monthly constants
      queryClient.invalidateQueries({ queryKey: ["monthlyConstants"] });
    },
  });

  const updateMonthlyConstants = useMutation({
    mutationFn: async (updatedConstants) => {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/campaignPlanning2/monthDefaults/${updatedConstants.id}`,
        updatedConstants,
        config,
      );
      return response.data.data;
    },
    onSuccess: () => {
      // Invalidate the query to refetch the monthly constants
      queryClient.invalidateQueries({ queryKey: ["monthlyConstants"] });
    },
    onError: (error) => {
      console.error("Error updating monthly constants:", error);
      // Optionally, you can show an error notification here
    },
  });

  // (DataState and useEffect to sync it are still good practice)
  const [DataState, setDataState] = useState([]);
  useEffect(() => {
    if (getCampaignByTypeAndDate.data) {
      setDataState(getCampaignByTypeAndDate.data);
    }
  }, [getCampaignByTypeAndDate.data]);

  const createCampaign = useMutation({
    mutationFn: async (newCampaign) => {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/campaignPlanning2`,
        newCampaign,
        config,
      );
      return response.data.data;
    },
    onSuccess: () => {
      // Update DataState with the newly created campaign
      queryClient.invalidateQueries({ queryKey: queryKey });
    },
    onError: (error) => {
      console.error("Error creating campaign:", error);
    },
  });

  const updateCampaign = useMutation({
    mutationFn: async (updatedCampaign) => {
      const { id, ...payload } = updatedCampaign;
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/campaignPlanning2/${id}`,
        payload,
        config,
      );
      return response.data.data;
    },
    onMutate: async (updatedCampaign) => {
      // This part is correct
      await queryClient.cancelQueries({ queryKey: queryKey });
      const previousData = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (oldData) =>
        (oldData || []).map(
          (
            row, // Add safety for undefined oldData
          ) =>
            row.id === updatedCampaign.id
              ? { ...row, ...updatedCampaign }
              : row,
        ),
      );
      return { previousData };
    },

    // --- THIS IS THE FIX ---
    // onError runs if the mutation fails.
    onError: (err, updatedCampaign, context) => {
      // ALWAYS check if context and previousData exist before rolling back.
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
      console.error("Update failed, rolling back if possible:", err);
      // You can also trigger an error notification here.
    },

    onSettled: () => {
      // This part is correct and very important. It always re-syncs with the server.
      queryClient.invalidateQueries({ queryKey: queryKey });
    },
  });

  const [monthDefaults, setMonthDefaults] = useState({
    workingDays: 0,
    workingHoursPerDay: 0,
  });

  const columnGroupingModel = React.useMemo(
    () => [
      {
        groupId: "Client Information",
        headerClassName: "group-blue", // CSS class for styling
        children: [
          { field: "client" },
          { field: "leadsType" },
          { field: "product" },
          { field: "campaignLiveDate" },
          { field: "linkedCallCentreCampaignIds" },
        ],
      },
      {
        groupId: "Capacity",
        headerClassName: "group-orange",
        children: [
          { field: "fullCapacity" },

          { field: "currentSeats" },
          { field: "hours" },
        ],
      },
      {
        groupId: "Income",
        headerClassName: "group-light-blue",
        children: [
          { field: "averagePremium" },
          { field: "biPerSale" },
          { field: "biPerHour" },
          { field: "biPerSeat" },
          { field: "totalBi" },
        ],
      },

      {
        groupId: "Sales",
        headerClassName: "group-sales-green",
        children: [
          { field: "salesPerHour" },
          { field: "sales" },
          { field: "totalPremium" },
          { field: "salesToContactRatio" },
          { field: "salesPerLead" },
        ],
      },

      {
        groupId: "Lead Requirements",
        headerClassName: "group-green",
        children: [
          { field: "contactToLeads" },
          { field: "leadsRequiredPerSeat" },
          { field: "totalLeadsRequired" },
          { field: "confirmedLeads" },
          { field: "requiredLessConfirmedLeads" },
        ],
      },
      {
        groupId: "Dependency Analysis",
        headerClassName: "group-purple",
        children: [
          { field: "additionalSeatsRequiredToMaxLeads" },
          { field: "capacityIncomeRisk" },
          { field: "additionalLeadsRequiredToMaxSeats" },
          { field: "leadsIncomeRisk" },
        ],
      },
    ],
    [],
  );

  const fieldToGroupMap = React.useMemo(() => {
    const map = {};

    columnGroupingModel.forEach((group) => {
      // Extract the color name (e.g., 'blue') from the class name (e.g., 'group-blue')
      const colorName = group.headerClassName.replace("group-", "");

      group.children.forEach((child) => {
        // Map the field name to its color
        map[child.field] = colorName;
      });
    });
    return map;
  }, [columnGroupingModel]);

  const getEditableCellClassName = React.useCallback(
    (params) => {
      // Only style cells that are actually editable
      if (!params.isEditable) {
        return "";
      }

      // Look up the color for the current field using our map
      const groupColor = fieldToGroupMap[params.field];

      // Return a specific class like 'editable-cell-blue', 'editable-cell-orange', etc.
      return groupColor
        ? `editable-cell editable-cell-${groupColor}`
        : "editable-cell";
    },
    [fieldToGroupMap],
  );

  const columns = React.useMemo(
    () => [
      // --- Pinned Utility Columns ---

      {
        field: "Edit",
        headerName: "Edit",
        width: 70,
        align: "center",
        renderCell: (params) => {
          if (params.row.id) {
            return (
              <CampaignGroup
                updateCampaign={updateCampaign.mutateAsync}
                campaignGroup={params.row}
                monthDefaults={monthDefaults}
                selectedCard={selectedCard}
                setDataState={setDataState}
                refetchFunction={getCampaignByTypeAndDate.refetch}
                deletePlanningCampaign={deletePlanningCampaign.mutateAsync}
              />
            );
          }
        },
      },
      {
        field: "name",
        headerName: "Name",
        width: 150,
        align: "center",
        pinned: "left",
        // This will render the word "Total" in that specific footer cell.
        renderCell: (params) => {
          if (params.row.id) {
            return <strong>{params.value}</strong>;
          }
          return <strong>Totals</strong>;
        },
      },

      // --- Blue Group: Client Information ---
      { field: "client", headerName: "Client", width: 150, align: "center" },
      {
        field: "leadsType",
        headerName: "Leads Type",
        width: 150,
        align: "center",
        pinned: "left",
      },
      {
        field: "linkedCallCentreCampaignIds",
        headerName: "Linked Campaigns",
        width: 200,
        align: "center",
        renderCell: (params) => {
          const linkedCampaigns = params.value || [];
          if (params.row.id) {
            return (
              <Typography variant="body2">
                {linkedCampaigns.length > 0
                  ? linkedCampaigns.join(", ")
                  : "No Linked Campaigns"}
              </Typography>
            );
          }
        },
      },
      { field: "product", headerName: "Product", width: 150, align: "center" },
      // Note: The image says "Live/start day", using activeStatus as the closest match
      {
        field: "campaignLiveDate",
        headerName: "Live Date/Live",
        width: 100,
        align: "center",
        renderCell: (params) => {
          const liveDate = params.value;

          if (liveDate && new Date(liveDate) <= new Date()) {
            return (
              <Typography variant="body2" color="secondary" fontWeight="bold">
                Live
              </Typography>
            );
          } else
            return (
              <Typography variant="body2" color="error">
                {liveDate}
              </Typography>
            );
        },
      },
      {
        field: "fullCapacity",
        headerName: "Full Capacity",
        type: "number",
        width: 120,
        align: "center",
        editable: true,
        cellClassName: getEditableCellClassName,
        valueFormatter: (params) =>
          typeof params.value === "number"
            ? `${params.value.toFixed(0)} %`
            : "",
      },

      {
        field: "currentSeats",
        headerName: "Current Seats",
        type: "number",
        width: 150,
        align: "center",
        editable: true,
        cellClassName: getEditableCellClassName,
      },

      // --- Orange Group: BI & Sales Metrics ---
      {
        field: "hours",
        headerName: "Hours",
        type: "number",
        align: "center",
        width: 100,
        valueGetter: (params) => calculator.hours(params.row, monthDefaults),
      },
      {
        field: "averagePremium",
        headerName: "Average Premium",
        type: "number",
        align: "center",
        width: 150,
        editable: true,
        cellClassName: getEditableCellClassName,
        valueFormatter: (params) =>
          typeof params.value === "number"
            ? currencyFormatter(params.value)
            : "",
      },
      {
        field: "biPerSale",
        headerName: "BI Per Sale",
        type: "number",
        align: "center",
        width: 150,
        editable: true,
        cellClassName: getEditableCellClassName,
        valueFormatter: (params) =>
          typeof params.value === "number"
            ? currencyFormatter(params.value)
            : "",
      },
      {
        field: "biPerHour",
        headerName: "BI Per Hour",
        type: "number",
        align: "center",
        width: 150,
        valueGetter: (params) => calculator.biPerHour(params.row),
        valueFormatter: (params) =>
          typeof params.value === "number"
            ? currencyFormatter(params.value)
            : "",
      },
      {
        field: "biPerSeat",
        headerName: "BI Per Seat",
        type: "number",
        align: "center",
        width: 150,
        valueGetter: (params) =>
          calculator.biPerSeat(params.row, monthDefaults),
        valueFormatter: (params) =>
          typeof params.value === "number"
            ? currencyFormatter(params.value)
            : "",
      },
      {
        field: "totalBi",
        headerName: "Total BI",
        type: "number",
        align: "center",
        width: 150,
        valueGetter: (params) => calculator.totalBi(params.row, monthDefaults),
        valueFormatter: (params) =>
          typeof params.value === "number"
            ? currencyFormatter(params.value)
            : "",
      },
      {
        field: "salesPerHour",
        headerName: "Sales Per Hour",
        type: "number",
        width: 150,
        align: "center",
        editable: true,
        cellClassName: getEditableCellClassName,
      },
      {
        field: "sales",
        headerName: "Sales",
        type: "number",
        width: 100,
        valueGetter: (params) => calculator.sales(params.row, monthDefaults),
      },
      {
        field: "totalPremium",
        headerName: "Total Premium",
        type: "number",
        align: "center",
        width: 150,
        valueGetter: (params) =>
          calculator.totalPremium(params.row, monthDefaults),
        valueFormatter: (params) =>
          typeof params.value === "number"
            ? currencyFormatter(params.value)
            : "",
      },

      // --- Light Orange/Peach Group: Conversion Ratios ---
      {
        field: "salesToContactRatio",
        headerName: "Sales To Contact Ratio",
        type: "number",
        width: 200,
        align: "center",
        editable: true,
        cellClassName: getEditableCellClassName,
        valueFormatter: (params) =>
          typeof params.value === "number"
            ? `${params.value.toFixed(1)} %`
            : "",
      },
      {
        field: "salesPerLead",
        headerName: "Sales/Lead",
        type: "number",
        align: "center",
        width: 150,
        valueGetter: (params) => calculator.salesPerLeadRatio(params.row),
        valueFormatter: (params) =>
          typeof params.value === "number"
            ? `${(params.value * 100).toFixed(2)} %`
            : "",
      },

      // --- Purple/Pink Group: Lead Requirements ---
      {
        field: "contactToLeads",
        headerName: "Contact/Leads",
        type: "number",
        align: "center",
        width: 150,
        editable: true,
        cellClassName: getEditableCellClassName,
        valueFormatter: (params) =>
          typeof params.value === "number"
            ? `${params.value.toFixed(1)} %`
            : "",
      },
      {
        field: "leadsRequiredPerSeat",
        headerName: "Leads Required Per Seat",
        type: "number",
        width: 200,
        align: "center",
        valueGetter: (params) =>
          calculator.leadsRequiredPerSeat(params.row, monthDefaults),
      },
      {
        field: "totalLeadsRequired",
        headerName: "Total Leads Required",
        type: "number",
        width: 200,
        align: "center",
        valueGetter: (params) =>
          calculator.totalLeadsRequired(params.row, monthDefaults),
      },
      {
        field: "confirmedLeads",
        headerName: "Confirmed Leads",
        type: "number",
        align: "center",
        width: 150,
        editable: true,
        cellClassName: getEditableCellClassName,
      },
      {
        field: "requiredLessConfirmedLeads",
        headerName: "Required Less Confirmed Leads",
        align: "center",
        type: "number",
        width: 250,
        valueGetter: (params) =>
          calculator.requiredLessConfirmedLeads(params.row, monthDefaults),
        renderCell: (params) => {
          const value = params.value;
          // If the value is negative, display it in red
          return (
            <span
              style={{
                color: value < 0 ? "red" : "inherit",
                fontWeight: "bold",
              }}
            >
              {value}
            </span>
          );
        },
      },

      // --- Green Group: Risk & Capacity Analysis ---
      {
        field: "additionalSeatsRequiredToMaxLeads",
        headerName: "Additional Seats Required To Max Leads",
        type: "number",
        align: "center",
        width: 250,
        valueGetter: (params) =>
          calculator.AdditionalSeatsRequiredToMaxLeads(
            params.row,
            monthDefaults,
          ),
      },
      {
        field: "capacityIncomeRisk",
        headerName: "Capacity Income Risk",
        align: "center",
        type: "number",
        width: 200,
        valueGetter: (params) =>
          calculator.capacityIncomeRisk(params.row, monthDefaults),

        renderCell: (params) => {
          const value = params.value;
          // If the value is Positive, display it in red
          return (
            <span
              style={{
                color: value > 0 ? "red" : "inherit",
                fontWeight: "bold",
              }}
            >
              {currencyFormatter(value)}
            </span>
          );
        },
      },
      {
        field: "additionalLeadsRequiredToMaxSeats",
        headerName: "Additional Leads Required To Max Seats",
        type: "number",
        width: 250,
        align: "center",
        valueGetter: (params) =>
          calculator.additionalLeadsRequiredToMaxSeats(
            params.row,
            monthDefaults,
          ),
      },
      {
        field: "leadsIncomeRisk",
        headerName: "Leads Income Risk",
        type: "number",
        align: "center",
        width: 200,
        valueGetter: (params) =>
          calculator.leadIncomeRisk(params.row, monthDefaults),
        renderCell: (params) => {
          const value = params.value;
          // If the value is Positive, display it in red
          return (
            <span
              style={{
                color: value > 0 ? "red" : "inherit",
                fontWeight: "bold",
              }}
            >
              {currencyFormatter(value)}
            </span>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Created At",
        hide: true, // This makes the column exist for sorting but invisible to the user
      },
    ],
    [
      getEditableCellClassName,
      updateCampaign.mutateAsync,
      monthDefaults,
      getCampaignByTypeAndDate.refetch,
      selectedCard,
      deletePlanningCampaign.mutateAsync,
    ],
  );

  // const FormatDataForSaving = () => {
  //   let formattedData = DataState.map((row) => {
  //     // Create a new object with only the fields we want to save
  //     return {
  //       ...row,
  //       planningType: selectedCard.type, // Add the selected card type
  //       date: selectedCard.date, // Add the selected card date
  //       hours: calculator.hours(row, monthDefaults),
  //       sales: calculator.sales(row, monthDefaults),
  //       salesPerLeadRatio: calculator.salesPerLeadRatio(row),
  //       totalBi: calculator.totalBi(row, monthDefaults),
  //       biPerHour: calculator.biPerHour(row),
  //       biPerSeat: calculator.biPerSeat(row, monthDefaults),
  //       totalPremium: calculator.totalPremium(row, monthDefaults),
  //       leadsRequiredPerSeat: calculator.leadsRequiredPerSeat(
  //         row,
  //         monthDefaults
  //       ),
  //       totalLeadsRequired: calculator.totalLeadsRequired(row, monthDefaults),
  //       requiredLessConfirmedLeads: calculator.requiredLessConfirmedLeads(
  //         row,
  //         monthDefaults
  //       ),
  //       additionalSeatsRequiredToMaxLeads:
  //         calculator.AdditionalSeatsRequiredToMaxLeads(row, monthDefaults),
  //       capacityIncomeRisk: calculator.capacityIncomeRisk(row, monthDefaults),
  //       additionalLeadsRequiredToMaxSeats:
  //         calculator.additionalLeadsRequiredToMaxSeats(row, monthDefaults),
  //       leadIncomeRisk: calculator.leadIncomeRisk(row, monthDefaults),
  //       workingDays: monthDefaults.workingDays,
  //       workingHoursPerDay: monthDefaults.workingHoursPerDay,
  //       // Add any other fields you want to save
  //     };
  //   });

  //   console.log("Formatted Data for Saving:", formattedData);
  // };

  const handleProcessRowUpdate = async (updatedRow, originalRow) => {
    // This function is triggered when a user finishes editing a cell.
    try {
      // Call the mutation. The optimistic update logic will handle the UI instantly.
      await updateCampaign.mutateAsync(updatedRow);
      // Return the updated row to the DataGrid to confirm the change.
      return updatedRow;
    } catch (error) {
      // If the mutation fails, it will throw an error.
      // We return the originalRow to tell the DataGrid to revert the cell's value.
      console.error("Row update failed:", error);
      return originalRow;
    }
  };

  const averagePremium = {
    label: "AP Calc",
    getCellValue: ({ row }) => ({
      totalPremium: calculator.totalPremium(row, monthDefaults),
      sales: calculator.sales(row, monthDefaults),
    }),
    apply: ({ values }) => {
      let totalPremium = 0;
      let sales = 0;
      values.forEach((value) => {
        if (value) {
          sales += value.sales;
          totalPremium += value.totalPremium;
        }
      });
      return totalPremium / sales || 0; // Avoid division by zero
    },
    columnTypes: ["number"],
  };

  const biPerSale = {
    label: "BI per Sale",
    getCellValue: ({ row }) => ({
      totalBi: calculator.totalBi(row, monthDefaults),
      sales: calculator.sales(row, monthDefaults),
    }),
    apply: ({ values }) => {
      let totalBi = 0;
      let sales = 0;
      values.forEach((value) => {
        if (value) {
          sales += value.sales;
          totalBi += value.totalBi;
        }
      });
      return totalBi / sales || 0; // Avoid division by zero
    },
    columnTypes: ["number"],
  };

  const biPerHour = {
    label: "BI per Hour",
    getCellValue: ({ row }) => ({
      totalBi: calculator.totalBi(row, monthDefaults),
      hours: calculator.hours(row, monthDefaults),
    }),
    apply: ({ values }) => {
      let totalBi = 0;
      let hours = 0;
      values.forEach((value) => {
        if (value) {
          hours += value.hours;
          totalBi += value.totalBi;
        }
      });
      return totalBi / hours || 0; // Avoid division by zero
    },
    columnTypes: ["number"],
  };

  const biPerSeat = {
    label: "BI per Seat",
    getCellValue: ({ row }) => ({
      totalBi: calculator.totalBi(row, monthDefaults),
      seats: row.currentSeats || 0, // Use currentSeats for the seat count
    }),
    apply: ({ values }) => {
      let totalBi = 0;
      let seats = 0;
      values.forEach((value) => {
        if (value) {
          seats += value.seats;
          totalBi += value.totalBi;
        }
      });
      return totalBi / seats || 0; // Avoid division by zero
    },
    columnTypes: ["number"],
  };

  const salesPerHour = {
    label: "Sales per Hour",
    getCellValue: ({ row }) => ({
      sales: calculator.sales(row, monthDefaults),
      hours: calculator.hours(row, monthDefaults),
    }),
    apply: ({ values }) => {
      let sales = 0;
      let hours = 0;
      values.forEach((value) => {
        if (value) {
          hours += value.hours;
          sales += value.sales;
        }
      });
      return (sales / hours || 0).toFixed(2); // Avoid division by zero
    },
    columnTypes: ["number"],
  };

  const salesPerLead = {
    label: "Sales per Lead",
    getCellValue: ({ row }) => ({
      sales: calculator.sales(row, monthDefaults),
      totalLeadsRequired: calculator.totalLeadsRequired(row, monthDefaults),
    }),
    apply: ({ values }) => {
      let sales = 0;
      let totalLeadsRequired = 0;
      values.forEach((value) => {
        if (value) {
          totalLeadsRequired += value.totalLeadsRequired;
          sales += value.sales;
        }
      });
      return sales / totalLeadsRequired || 0; // Avoid division by zero
    },
    columnTypes: ["number"],
  };

  const salesPerContact = {
    label: "Sales per Contact",
    getCellValue: ({ row }) => ({
      sales: calculator.sales(row, monthDefaults),
      totalLeadsRequired: calculator.totalLeadsRequired(row, monthDefaults),
      contactToLeads: row.contactToLeads || 0,
    }),
    apply: ({ values }) => {
      let sales = 0;
      let contactToLeads = 0;
      values.forEach((value) => {
        if (value) {
          // console.log("Value for salesPerContact aggregation:", value);
          sales += value.sales;
          contactToLeads +=
            value.totalLeadsRequired * (value.contactToLeads / 100.0 || 0); // Convert ratio back to contact count
        }
      });
      return (sales / contactToLeads) * 100 || 0; // Avoid division by zero
    },
    columnTypes: ["number"],
  };

  const contactsPerLeads = {
    label: "Contact/Leads",
    getCellValue: ({ row }) => ({
      totalLeadsRequired: calculator.totalLeadsRequired(row, monthDefaults),
      contactToLeads: row.contactToLeads || 0,
    }),
    apply: ({ values }) => {
      let contactToLeads = 0;
      let leadsRequired = 0;
      values.forEach((value) => {
        if (value) {
          // console.log("Value for salesPerContact aggregation:", value);
          contactToLeads +=
            value.totalLeadsRequired * (value.contactToLeads / 100.0 || 0); // Convert ratio back to contact count
          leadsRequired += value.totalLeadsRequired;
        }
      });
      return (contactToLeads / leadsRequired) * 100 || 0; // Avoid division by zero
    },
    columnTypes: ["number"],
  };

  const leadsPerSeat = {
    label: "Leads per Seat",
    getCellValue: ({ row }) => ({
      currentSeats: row.currentSeats || 0,
      totalLeadsRequired: calculator.totalLeadsRequired(row, monthDefaults),
    }),
    apply: ({ values }) => {
      let currentSeats = 0;
      let totalLeadsRequired = 0;
      values.forEach((value) => {
        if (value) {
          totalLeadsRequired += value.totalLeadsRequired;
          currentSeats += value.currentSeats;
        }
      });
      return totalLeadsRequired / currentSeats || 0; // Avoid division by zero
    },
    columnTypes: ["number"],
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <TypeCards
          month={month}
          selectedCard={selectedCard}
          setSelectedCard={setSelectedCard}
        />
      </Grid>
      <Grid item xs={12} sx={{ my: 2 }}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          {selectedCard.title && (
            <CampaignGroup
              deletePlanningCampaign={deletePlanningCampaign.mutateAsync}
              monthDefaults={monthDefaults}
              createCampaign={createCampaign.mutateAsync}
              selectedCard={selectedCard}
              refetchFunction={getCampaignByTypeAndDate.refetch}
            />
          )}
          {
            // if there is no data in DataState, show the DuplicatePrevious component
            DataState.length === 0 && (
              <DuplicatePrevious
                selectedCard={selectedCard}
                onSuccess={() => {
                  getCampaignByTypeAndDate.refetch();
                }}
              />
            )
          }

          <MonthlyConstants
            createMonthlyConstants={createMonthlyConstants.mutateAsync}
            month={month}
            monthDefaults={monthDefaults}
            updateMonthlyConstants={updateMonthlyConstants.mutateAsync}
            monthDefaultsCheck={monthDefaultsCheck}
            selectedCard={selectedCard}
          />
        </Stack>
        {getMonthlyConstants.isLoading && <LinearProgress />}
        {/* if not monthly constants, show Alert  */}
        {getMonthlyConstants.isError && (
          <Alert sx={{ mt: 2 }} severity="warning" variant="outlined">
            No Monthly constants set
          </Alert>
        )}
      </Grid>
      <Grid item xs={12}>
        {getCampaignByTypeAndDate.isLoading && <LinearProgress />}
        <CustomPaper>
          {DataState && DataState.length > 0 && (
            <DataGridPremium
              rows={DataState}
              getRowId={(row) => row.id}
              columns={columns} // Use the reordered columns array
              processRowUpdate={handleProcessRowUpdate}
              onProcessRowUpdateError={(error) => console.error(error)} // Good practice to add an error handler
              pinnedColumns={{ left: ["Edit", "name"] }}
              experimentalFeatures={{ columnGrouping: true }}
              columnGroupingModel={columnGroupingModel}
              // aggregationFunctions={aggregationFunctionsByField}
              // getAggregationFunctions={(colDef) =>
              //   aggregationFunctionsByField[colDef.field]
              // }
              aggregationFunctions={{
                ...GRID_AGGREGATION_FUNCTIONS,
                averagePremium,
                biPerSale,
                biPerHour,
                biPerSeat,
                salesPerHour,
                salesPerLead,
                leadsPerSeat,
                salesPerContact,
                contactsPerLeads,
              }}
              showColumnVerticalBorder={true}
              // showCellVerticalBorder={true}
              initialState={{
                aggregation: {
                  model: {
                    // Sums

                    currentSeats: "sum",
                    hours: "sum",
                    sales: "sum",
                    totalPremium: "sum",
                    totalLeadsRequired: "sum",
                    confirmedLeads: "sum",
                    requiredLessConfirmedLeads: "sum",
                    additionalSeatsRequiredToMaxLeads: "sum",
                    capacityIncomeRisk: "sum",
                    additionalLeadsRequiredToMaxSeats: "sum",
                    leadsIncomeRisk: "sum",
                    totalBi: "sum",

                    // Averages
                    averagePremium: "averagePremium",
                    biPerSale: "biPerSale",
                    biPerHour: "biPerHour",
                    biPerSeat: "biPerSeat",
                    salesPerHour: "salesPerHour",
                    salesToContactRatio: "salesPerContact",
                    salesPerLead: "salesPerLead",
                    contactToLeads: "contactsPerLeads",
                    leadsRequiredPerSeat: "leadsPerSeat",
                  },
                },
                sorting: {
                  sortModel: [{ field: "createdAt", sort: "asc" }], // 'asc' for ascending, 'desc' for descending
                },
              }}
            />
          )}
        </CustomPaper>
      </Grid>
      <AlertPopup
        open={createCampaign.isError || updateCampaign.isError}
        handleClose={() => {
          createCampaign.reset();
          updateCampaign.reset();
        }}
        severity="error"
        message={
          createCampaign.error?.response?.data?.message ||
          updateCampaign.error?.response?.data?.message ||
          "An error occurred while processing your request."
        }
      />

      <AlertPopup
        open={createCampaign.isSuccess}
        handleClose={() => {
          createCampaign.reset();
        }}
        severity="success"
        message={
          createCampaign.data?.message || "Operation completed successfully."
        }
      />

      <Snackbar
        open={updateCampaign.isSuccess}
        autoHideDuration={1000}
        onClose={() => updateCampaign.reset()}
        message="updated successfully!"
        severity="success"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
      <AlertPopup
        open={updateCampaign.isError}
        severity="error"
        message={
          updateCampaign.error?.response?.data?.message ||
          "An error occurred while updating the campaign."
        }
      />

      <AlertPopup
        open={getCampaignByTypeAndDate.isError}
        handleClose={() => getCampaignByTypeAndDate.reset()}
        severity="error"
        message={
          getCampaignByTypeAndDate.error?.response?.data?.message ||
          "An error occurred while fetching campaigns."
        }
      />

      <AlertPopup
        open={createMonthlyConstants.isError}
        handleClose={() => createMonthlyConstants.reset()}
        severity="error"
        message={
          createMonthlyConstants.error?.response?.data?.message ||
          "An error occurred while creating monthly constants."
        }
      />

      <AlertPopup
        open={updateMonthlyConstants.isError}
        handleClose={() => updateMonthlyConstants.reset()}
        severity="error"
        message={
          updateMonthlyConstants.error?.response?.data?.message ||
          "An error occurred while updating monthly constants."
        }
      />

      <AlertPopup
        open={createMonthlyConstants.isSuccess}
        handleClose={() => createMonthlyConstants.reset()}
        severity="success"
        message={
          createMonthlyConstants.data?.message ||
          "Monthly constants created successfully."
        }
      />

      <AlertPopup
        open={updateMonthlyConstants.isSuccess}
        handleClose={() => updateMonthlyConstants.reset()}
        severity="success"
        message={
          updateMonthlyConstants.data?.message ||
          "Monthly constants updated successfully."
        }
      />
    </Grid>
  );
};

PlanningContainer.propTypes = {
  month: PropTypes.string.isRequired,
};

export default withAuth(PlanningContainer);

const CustomPaper = ({ children, ...props }) => {
  return (
    <Paper
      sx={{
        p: 0,
        height: "100%",
        minHeight: 600,
        width: "100%",
        maxWidth: "99vw",
        overflowX: "auto",

        mx: 0,

        "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnGroupHeader": {
          paddingLeft: 0,
          paddingRight: 0,

          mx: 0, // Remove horizontal padding
        },

        "& .MuiDataGrid-columnHeaderTitleContainer": {
          py: 1, // Use vertical padding for height
          px: 1, // NO horizontal padding
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: 0,
        },

        "& .MuiDataGrid-columnHeaderTitle": {
          fontSize: "0.8rem",
          fontWeight: "bold",
          lineHeight: 1.3,
          whiteSpace: "normal",

          mx: 0,
        },
        "& .MuiDataGrid-columnGroupHeader .MuiDataGrid-columnHeaderTitle": {
          fontWeight: "600",
          fontSize: "0.85rem",
          mx: 0,
          textAlign: "center",
        },

        /* --- Group Header Colors (Unchanged) --- */
        "& .group-blue .MuiDataGrid-columnHeaderTitleContainer": {
          backgroundColor: "rgba(74, 169, 219, 0.15)",
          boxShadow: "inset 0 0 0 1px rgba(74, 169, 219, 0.7)",
        },
        "& .group-orange .MuiDataGrid-columnHeaderTitleContainer": {
          backgroundColor: "rgba(244, 176, 132, 0.15)",
          boxShadow: "inset 0 0 0 1px rgba(244, 176, 132, 0.7)",
        },
        "& .group-light-blue .MuiDataGrid-columnHeaderTitleContainer": {
          backgroundColor: "rgba(76, 224, 230, 0.15)",
          boxShadow: "inset 0 0 0 1px rgba(132, 227, 244, 0.7)",
        },
        // ... all your other color styles remain the same
        "& .group-sales-green .MuiDataGrid-columnHeaderTitleContainer": {
          backgroundColor: "rgba(45, 236, 109, 0.15)",
          boxShadow: "inset 0 0 0 1px rgba(39, 230, 103, 0.7)",
        },
        "& .group-peach .MuiDataGrid-columnHeaderTitleContainer": {
          backgroundColor: "rgba(248, 203, 173, 0.15)",
          boxShadow: "inset 0 0 0 1px rgba(248, 203, 173, 0.7)",
        },
        "& .group-purple .MuiDataGrid-columnHeaderTitleContainer": {
          backgroundColor: "rgba(217, 150, 148, 0.15)",
          boxShadow: "inset 0 0 0 1px rgba(217, 150, 148, 0.7)",
        },
        "& .group-green .MuiDataGrid-columnHeaderTitleContainer": {
          backgroundColor: "rgba(196, 215, 155, 0.15)",
          boxShadow: "inset 0 0 0 1px rgba(196, 215, 155, 0.7)",
        },
        "& .MuiDataGrid-columnGroupHeader": { borderBottom: "none" },

        /* --- Editable Cell Styles (Unchanged) --- */
        "& .editable-cell": {
          fontWeight: "bold",
        },
        // ... all your editable cell styles remain the same
        "& .editable-cell-blue": {
          backgroundColor: "rgba(74, 169, 219, 0.15)",
          boxShadow: "inset 0 0 0 1px rgba(74, 169, 219, 0.7)",
        },
        "& .editable-cell-orange": {
          backgroundColor: "rgba(244, 176, 132, 0.15)",
          boxShadow: "inset 0 0 0 1px rgba(244, 176, 132, 0.7)",
        },
        "& .editable-cell-light-blue": {
          backgroundColor: "rgba(76, 224, 230, 0.15)",
          boxShadow: "inset 0 0 0 1px rgba(132, 227, 244, 0.7)",
        },
        "& .editable-cell-sales-green": {
          backgroundColor: "rgba(45, 236, 109, 0.15)",
          boxShadow: "inset 0 0 0 1px rgba(39, 230, 103, 0.7)",
        },
        "& .editable-cell-peach": {
          backgroundColor: "rgba(248, 203, 173, 0.15)",
          boxShadow: "inset 0 0 0 1px rgba(248, 203, 173, 0.7)",
        },
        "& .editable-cell-purple": {
          backgroundColor: "rgba(217, 150, 148, 0.15)",
          boxShadow: "inset 0 0 0 1px rgba(217, 150, 148, 0.7)",
        },
        "& .editable-cell-green": {
          backgroundColor: "rgba(196, 215, 155, 0.15)",
          boxShadow: "inset 0 0 0 1px rgba(196, 215, 155, 0.7)",
        },
        "& .Mui-editing": {
          backgroundColor: "rgba(144, 202, 249, 0.3)",
          boxShadow: (theme) => `inset 0 0 0 2px ${theme.palette.primary.main}`,
        },
      }}
    >
      {children}
    </Paper>
  );
};
